import { useState } from 'react';
import { useAuthStore } from '@quickbee/auth';

/**
 * The tenant's wordmark — their uploaded logo, or their name set as type.
 *
 * One component because the rule matters more than the three places it applies: every
 * host this app is served on is a white-labelled tenant, QuickBee's own included, so
 * the platform's logo is never the right thing to draw here. It was drawn three times
 * over — as the `||` fallback while branding was still resolving (the swap a saver
 * watched on every cold load), as the `onError` fallback, and permanently for any
 * tenant who had uploaded no logo at all.
 *
 * The fallback is now the tenant's own NAME. Before branding resolves there is no name
 * either, and the space is simply empty for that moment — an unbranded frame, never
 * another company's brand.
 */
interface TenantWordmarkProps {
  /** Classes for the `<img>` when the tenant has a logo. */
  imgClassName?: string;
  /** Classes for the text wordmark used when they have not. */
  textClassName?: string;
}

export function TenantWordmark({ imgClassName, textClassName }: TenantWordmarkProps) {
  const tenantConfig = useAuthStore((s) => s.tenantConfig);
  const [logoFailed, setLogoFailed] = useState(false);

  const name = tenantConfig?.appName || tenantConfig?.name || '';
  const logoUrl = tenantConfig?.logoUrl;

  if (logoUrl && !logoFailed) {
    return (
      <img
        src={logoUrl}
        alt={name}
        // A tenant logo that fails to load degrades to their name, not to a
        // broken-image glyph and not to the platform's mark.
        onError={() => setLogoFailed(true)}
        className={imgClassName}
      />
    );
  }

  return <span className={textClassName}>{name}</span>;
}

